/**
 * Notification System v2 — shared React UI (same file in care-admin, care-provider
 * and care). Plain React + notify.css so it renders the same under antd 4 and 5,
 * in light and dark mode.
 *
 *   <NotifyProvider client={client} navigate={navigate}>
 *     <NotificationBell />           in the header
 *     <NotificationToasts />         once, at the root
 *     <NotificationCentre />         on the /notifications route
 *   </NotifyProvider>
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import './notify.css';

const NotifyContext = createContext(null);

export const CATEGORY_LABELS = {
  pa: 'Pre-authorisation',
  claims: 'Claims',
  organisations: 'Organisations',
  account: 'Account',
  payments: 'Payments',
  clients: 'Clients',
  system: 'System',
};

const NEUTRAL_NO_ACCESS = 'You no longer have access to this item.';

export function formatWhen(iso) {
  if (!iso) return '';
  try {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Africa/Lagos', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    }).format(new Date(iso));
  } catch (e) {
    return iso;
  }
}

export function NotifyProvider({ client, navigate, children }) {
  const [state, setState] = useState(() => (client ? client.getState() : null));
  const [notice, setNotice] = useState(null);
  const listeners = useRef(new Set());

  useEffect(() => {
    if (!client) return undefined;
    const off = client.subscribe((type, payload, s) => {
      setState(s);
      listeners.current.forEach((fn) => fn(type, payload));
    });
    client.start();
    return () => { off(); };
  }, [client]);

  const on = useCallback((fn) => { listeners.current.add(fn); return () => listeners.current.delete(fn); }, []);

  /** Open a notification: server access check first (§5.3), then navigate. */
  const openItem = useCallback(async (id) => {
    if (!client) return;
    const res = await client.open(id);
    if (res && res.allowed && res.destination && res.destination.path) {
      navigate(res.destination.path);
    } else if (res && res.allowed) {
      navigate('/notifications');
    } else {
      setNotice(res?.message || NEUTRAL_NO_ACCESS);
    }
  }, [client, navigate]);

  const value = useMemo(() => ({ client, state, on, openItem, navigate, notice, setNotice }), [client, state, on, openItem, navigate, notice]);
  if (!client) return children;
  return <NotifyContext.Provider value={value}>{children}</NotifyContext.Provider>;
}

export function useNotify() {
  return useContext(NotifyContext);
}

// ------------------------------------------------------------------- bell
export function NotificationBell({ className = '', centrePath = '/notifications' }) {
  const ctx = useNotify();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);
  const buttonRef = useRef(null);

  const load = useCallback(async () => {
    if (!ctx) return;
    setLoading(true);
    try {
      const res = await ctx.client.api.page({ limit: 6 });
      setItems(res?.data?.items || []);
    } finally {
      setLoading(false);
    }
  }, [ctx]);

  useEffect(() => {
    if (!ctx) return undefined;
    return ctx.on((type, payload) => {
      if (type === 'new') setItems((prev) => [payload, ...prev.filter((p) => p.id !== payload.id)].slice(0, 6));
      if (type === 'sync') {
        if (payload.all_deleted) setItems([]);
        if (payload.deleted_ids) setItems((prev) => prev.filter((p) => !payload.deleted_ids.includes(p.id)));
        if (payload.all_read) setItems((prev) => prev.map((p) => ({ ...p, read: true })));
        if (payload.read_ids) setItems((prev) => prev.map((p) => (payload.read_ids.includes(p.id) ? { ...p, read: true } : p)));
      }
    });
  }, [ctx]);

  useEffect(() => {
    if (!open) return undefined;
    load();
    const close = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target) && buttonRef.current && !buttonRef.current.contains(e.target)) setOpen(false);
    };
    const esc = (e) => { if (e.key === 'Escape') { setOpen(false); buttonRef.current?.focus(); } };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [open, load]);

  if (!ctx || !ctx.state?.enabled) return null;
  const unread = ctx.state.unread || 0;

  return (
    <div className={`nt-bell ${className}`}>
      <button
        ref={buttonRef}
        type="button"
        className="nt-bell-button"
        aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path fill="currentColor" d="M12 22a2.5 2.5 0 0 0 2.45-2h-4.9A2.5 2.5 0 0 0 12 22Zm7-6V11a7 7 0 0 0-5.5-6.84V3.5a1.5 1.5 0 0 0-3 0v.66A7 7 0 0 0 5 11v5l-2 2v1h18v-1l-2-2Z" />
        </svg>
        {unread > 0 && <span className="nt-badge" aria-hidden="true">{unread > 99 ? '99+' : unread}</span>}
      </button>
      {open && (
        <div ref={panelRef} className="nt-panel" role="dialog" aria-label="Notifications">
          <div className="nt-panel-head">
            <strong>Notifications</strong>
            <button type="button" className="nt-link" disabled={!unread} onClick={() => ctx.client.markRead('all')}>Mark all as read</button>
          </div>
          {loading && !items.length ? <p className="nt-empty">Loading…</p> : null}
          {!loading && !items.length ? <p className="nt-empty">No notifications</p> : null}
          <ul className="nt-panel-list">
            {items.map((it) => (
              <li key={it.id}>
                <button type="button" className={`nt-row ${it.read ? '' : 'nt-unread'}`} onClick={() => { setOpen(false); ctx.openItem(it.id); }}>
                  <span className="nt-row-title">{it.title}</span>
                  <span className="nt-row-msg">{it.message}</span>
                  <span className="nt-row-meta">{formatWhen(it.created_at)}</span>
                </button>
              </li>
            ))}
          </ul>
          <div className="nt-panel-foot">
            <button type="button" className="nt-link" onClick={() => { setOpen(false); ctx.navigate(centrePath); }}>View all notifications</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------- toasts
const TOAST_MS = 8000;

function Toast({ toast, onClose, onOpen }) {
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused) return undefined;
    const t = setTimeout(() => onClose(toast.key), TOAST_MS);
    return () => clearTimeout(t);
  }, [paused, toast.key, onClose]);
  const critical = toast.critical;
  return (
    <div
      className={`nt-toast ${critical ? 'nt-toast-critical' : ''}`}
      role={critical ? 'alert' : 'status'}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      onKeyDown={(e) => { if (e.key === 'Escape') onClose(toast.key); }}
    >
      <div className="nt-toast-body">
        <strong className="nt-toast-title">{toast.title}</strong>
        <span className="nt-toast-msg">{toast.message}</span>
      </div>
      <div className="nt-toast-actions">
        <button type="button" className="nt-btn nt-btn-primary" onClick={() => { onOpen(toast); onClose(toast.key); }}>Open</button>
        <button type="button" className="nt-btn" aria-label="Dismiss notification" onClick={() => onClose(toast.key)}>Dismiss</button>
      </div>
    </div>
  );
}

function summaryText(groups) {
  const total = groups.reduce((n, g) => n + g.count, 0);
  if (groups.length === 1) return `${total} new ${groups[0].title}`;
  if (groups.length <= 3) return groups.map((g) => `${g.count} ${g.title}`).join(', ');
  return `${total} new notifications`;
}

export function NotificationToasts({ centrePath = '/notifications' }) {
  const ctx = useNotify();
  const [toasts, setToasts] = useState([]);

  const close = useCallback((key) => setToasts((prev) => prev.filter((t) => t.key !== key)), []);

  useEffect(() => {
    if (!ctx) return undefined;
    return ctx.on((type, payload) => {
      if (type === 'popup') {
        setToasts((prev) => [...prev.filter((t) => t.kind !== 'item' || t.id !== payload.id), {
          key: `n-${payload.id}`, kind: 'item', id: payload.id, title: payload.title, message: payload.message, critical: payload.severity === 'critical',
        }].slice(-4));
      }
      if (type === 'summary') {
        // The burst's individual pop-ups are replaced by one summary (§4.5).
        setToasts((prev) => [...prev.filter((t) => t.kind === 'notice' || (t.kind === 'item' && !payload.ids.includes(t.id))), {
          key: 'summary', kind: 'summary', ids: payload.ids, title: 'New notifications', message: summaryText(payload.groups),
          critical: payload.groups.some((g) => g.critical),
        }]);
      }
    });
  }, [ctx]);

  useEffect(() => {
    if (!ctx?.notice) return;
    setToasts((prev) => [...prev, { key: `notice-${Date.now()}`, kind: 'notice', title: 'Notification', message: ctx.notice }]);
    ctx.setNotice(null);
  }, [ctx]);

  if (!ctx) return null;
  const openToast = (t) => {
    if (t.kind === 'item') ctx.openItem(t.id);
    if (t.kind === 'summary') ctx.navigate(`${centrePath}?ids=${t.ids.join(',')}`);
  };

  return (
    <div className="nt-toasts" aria-live="polite" aria-relevant="additions">
      {toasts.map((t) => (t.kind === 'notice'
        ? (
          <div key={t.key} className="nt-toast" role="status">
            <div className="nt-toast-body"><span className="nt-toast-msg">{t.message}</span></div>
            <div className="nt-toast-actions"><button type="button" className="nt-btn" onClick={() => close(t.key)}>Dismiss</button></div>
          </div>
        )
        : <Toast key={t.key} toast={t} onClose={close} onOpen={openToast} />))}
    </div>
  );
}

// ------------------------------------------------------------ permission prompt
const PROMPT_KEY = 'aman-notify-prompt-dismissed';
const PROMPT_SNOOZE_DAYS = { not_asked: 3, blocked: 30, install: 14 };

function isIosBrowserTab() {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;
  const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const standalone = window.navigator.standalone === true
    || (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches);
  return ios && !standalone;
}

function snoozed(kind) {
  try {
    const saved = JSON.parse(localStorage.getItem(PROMPT_KEY) || '{}');
    const until = Number(saved[kind] || 0);
    return until > Date.now();
  } catch (e) {
    return false;
  }
}

function snooze(kind) {
  try {
    const saved = JSON.parse(localStorage.getItem(PROMPT_KEY) || '{}');
    saved[kind] = Date.now() + (PROMPT_SNOOZE_DAYS[kind] || 3) * 86400000;
    localStorage.setItem(PROMPT_KEY, JSON.stringify(saved));
  } catch (e) { /* private mode: the prompt simply comes back next visit */ }
}

/**
 * Asks once, from a button, for permission to show system notifications, so
 * they arrive on the desktop or phone when this portal is closed or hidden.
 * Without it the browser is never registered and every push is skipped
 * ("no_subscription"): the preference defaults to on, but browsers only grant
 * permission from a click (§4.7). Also tells blocked browsers how to unblock,
 * and iPhone users that push needs the portal on the Home Screen.
 */
export function NotificationPermissionPrompt() {
  const ctx = useNotify();
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState(false);
  if (!ctx || hidden) return null;
  const { ready, enabled, webPush, preferences, pushStatus } = ctx.state;
  if (!ready || !enabled || !webPush?.configured || !preferences?.browser_push_enabled) return null;

  let kind = null;
  if (pushStatus === 'not_asked') kind = 'not_asked';
  else if (pushStatus === 'blocked') kind = 'blocked';
  else if (pushStatus === 'unsupported' && isIosBrowserTab()) kind = 'install';
  if (!kind || snoozed(kind)) return null;

  const dismiss = () => {
    snooze(kind);
    setHidden(true);
  };
  const turnOn = async () => {
    setBusy(true);
    try {
      const status = await ctx.client.enablePush();
      if (status === 'subscribed') {
        ctx.setNotice('Notifications are on. You will be notified on this device even when the portal is closed.');
        setHidden(true);
      } else if (status === 'blocked') {
        snooze('not_asked');
      }
    } finally {
      setBusy(false);
    }
  };

  const copy = {
    not_asked: {
      title: 'Get notified when the portal is closed',
      body: 'Allow notifications so new items reach you on this computer or phone, even when this tab is not open.',
    },
    blocked: {
      title: 'Notifications are blocked',
      body: 'This browser is blocking notifications for this site. Open the site settings (the icon next to the address bar), set Notifications to Allow, then reload the page.',
    },
    install: {
      title: 'Notifications on iPhone and iPad',
      body: 'Tap Share, then Add to Home Screen, and open the portal from the new icon. You can then turn on notifications there.',
    },
  }[kind];

  return (
    <div className="nt-permission" role="region" aria-label="Notification permission">
      <div className="nt-permission-text">
        <strong className="nt-permission-title">{copy.title}</strong>
        <span className="nt-permission-body">{copy.body}</span>
      </div>
      <div className="nt-permission-actions">
        {kind === 'not_asked' ? (
          <>
            <button type="button" className="nt-btn nt-btn-primary" onClick={turnOn} disabled={busy}>
              {busy ? 'Waiting for the browser…' : 'Turn on notifications'}
            </button>
            <button type="button" className="nt-btn" onClick={dismiss} disabled={busy}>Not now</button>
          </>
        ) : (
          <button type="button" className="nt-btn" onClick={dismiss}>Dismiss</button>
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------------------ preferences
const PUSH_STATUS_TEXT = {
  unsupported: 'This browser does not support browser notifications. On iPhone and iPad they only work for a web app added to the Home Screen.',
  not_configured: 'Browser notifications are not set up on the server yet. Ask the Aman HMO administrator to configure them (the Admin Portal notification settings page shows the status).',
  blocked: 'Blocked in your browser. To allow them, open the site settings (the icon next to the address bar), set Notifications to Allow, then reload this page.',
  not_asked: 'Your browser will ask for permission when you switch this on. If it does not, the permission was dismissed earlier: open the site settings next to the address bar and set Notifications to Allow.',
  granted: 'Allowed in your browser, but this browser is not registered yet. Click the switch to register it.',
  subscribed: 'On for this browser. You are notified here only while this portal is not open in a visible tab.',
  unknown: '',
};
const PUSH_STATUS_WARN = ['unsupported', 'not_configured', 'blocked', 'granted'];

export function NotificationPreferences() {
  const ctx = useNotify();
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);
  if (!ctx) return null;
  const { preferences, pushStatus, pushError } = ctx.state;
  const statusText = pushError || PUSH_STATUS_TEXT[pushStatus] || '';
  const statusWarn = !!pushError || PUSH_STATUS_WARN.includes(pushStatus);
  const canTest = preferences.browser_push_enabled && (pushStatus === 'subscribed' || pushStatus === 'granted');

  const runTest = async () => {
    setTestResult(null);
    const r = await ctx.client.testPush();
    setTestResult(r);
  };

  const togglePush = async () => {
    setBusy(true);
    try {
      // The preference defaults to on, but the browser is only registered once
      // permission is granted. Until then a click must ask, not switch it off.
      const registered = pushStatus === 'subscribed' || pushStatus === 'unsupported' || pushStatus === 'not_configured';
      if (preferences.browser_push_enabled && registered) {
        await ctx.client.updatePreferences({ browser_push_enabled: false });
      } else {
        // Saved even when the browser blocks it; nothing is sent until permission is granted (§3.5).
        await ctx.client.enablePush();
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="nt-prefs" role="group" aria-label="Notification preferences">
      <div className="nt-pref">
        <div>
          <span className="nt-pref-label" id="nt-pref-push">Browser notifications</span>
          <span className="nt-pref-help">Shown when this portal is not open in a visible tab.</span>
          {(preferences.browser_push_enabled || statusWarn) && statusText ? (
            <span className={`nt-pref-status ${statusWarn ? 'nt-warn' : ''}`} role={statusWarn ? 'alert' : undefined}>{statusText}</span>
          ) : null}
          {canTest ? (
            <span className="nt-pref-test">
              <button type="button" className="nt-link" onClick={runTest}>Send a test notification</button>
              {testResult ? <span className={`nt-pref-status ${testResult.ok ? '' : 'nt-warn'}`}>{testResult.message}</span> : null}
            </span>
          ) : null}
        </div>
        <button type="button" role="switch" aria-checked={!!preferences.browser_push_enabled} aria-labelledby="nt-pref-push"
          className={`nt-switch ${preferences.browser_push_enabled ? 'nt-on' : ''}`} disabled={busy} onClick={togglePush}>
          <span />
        </button>
      </div>
      <div className="nt-pref">
        <div>
          <span className="nt-pref-label" id="nt-pref-sound">Notification sound</span>
          <span className="nt-pref-help">A short sound when a new notification arrives. Pop-ups and the centre are not affected.</span>
        </div>
        <button type="button" role="switch" aria-checked={!!preferences.sound_enabled} aria-labelledby="nt-pref-sound"
          className={`nt-switch ${preferences.sound_enabled ? 'nt-on' : ''}`}
          onClick={() => { ctx.client.unlockSound(); ctx.client.updatePreferences({ sound_enabled: !preferences.sound_enabled }); }}>
          <span />
        </button>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ centre
export function NotificationCentre({ showFacility = false, title = 'Notifications', location }) {
  const ctx = useNotify();
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('all');
  const [category, setCategory] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [confirm, setConfirm] = useState(null);
  const [showPrefs, setShowPrefs] = useState(false);
  const search = location?.search ?? (typeof window !== 'undefined' ? window.location.search : '');
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const burstIds = params.get('ids');
  const openId = params.get('open');
  const openedRef = useRef(null);

  const load = useCallback(async (reset) => {
    if (!ctx) return;
    setLoading(true);
    try {
      const res = await ctx.client.api.page({
        limit: 30,
        status,
        ...(category ? { category } : {}),
        ...(burstIds ? { ids: burstIds } : {}),
        ...(!reset && cursor ? { cursor } : {}),
      });
      const next = res?.data?.items || [];
      setItems((prev) => (reset ? next : [...prev, ...next.filter((n) => !prev.some((p) => p.id === n.id))]));
      setCursor(res?.data?.next_cursor || null);
    } finally {
      setLoading(false);
    }
  }, [ctx, status, category, burstIds, cursor]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setSelected(new Set()); load(true); }, [ctx, status, category, burstIds]);

  // Push and pop-up clicks arrive as ?open=ID: check access on the server, then go (§5.3, §7).
  useEffect(() => {
    if (ctx && openId && openedRef.current !== openId) {
      openedRef.current = openId;
      ctx.openItem(Number(openId));
    }
  }, [ctx, openId]);

  useEffect(() => {
    if (!ctx) return undefined;
    return ctx.on((type, payload) => {
      if (type === 'new' && !burstIds && status !== 'read' && (!category || payload.category === category)) {
        setItems((prev) => (prev.some((p) => p.id === payload.id) ? prev : [payload, ...prev]));
      }
      if (type === 'sync') {
        if (payload.all_deleted) setItems([]);
        if (payload.deleted_ids) setItems((prev) => prev.filter((p) => !payload.deleted_ids.includes(p.id)));
        if (payload.all_read) setItems((prev) => prev.map((p) => ({ ...p, read: true })));
        if (payload.read_ids) setItems((prev) => prev.map((p) => (payload.read_ids.includes(p.id) ? { ...p, read: true } : p)));
      }
    });
  }, [ctx, burstIds, status, category]);

  if (!ctx) return null;
  if (!ctx.state?.enabled) {
    return <div className="nt-centre"><h1 className="nt-h1">{title}</h1><p className="nt-empty">Notifications are not switched on yet.</p></div>;
  }

  const toggle = (id) => setSelected((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const selectedIds = [...selected];
  const allSelected = items.length > 0 && items.every((i) => selected.has(i.id));
  const selectByRead = (read) => setSelected(new Set(items.filter((i) => i.read === read).map((i) => i.id)));

  const doRead = async (ids) => {
    await ctx.client.markRead(ids);
    setItems((prev) => prev.map((p) => (ids === 'all' || ids.includes(p.id) ? { ...p, read: true } : p)));
    setSelected(new Set());
  };
  const doDelete = async (ids) => {
    await ctx.client.remove(ids);
    setItems((prev) => (ids === 'all' ? [] : prev.filter((p) => !ids.includes(p.id))));
    setSelected(new Set());
    setConfirm(null);
  };

  return (
    <div className="nt-centre">
      <div className="nt-centre-head">
        <h1 className="nt-h1">{title}</h1>
        <span className="nt-muted">{ctx.state.unread} unread</span>
        <button type="button" className="nt-btn nt-ml-auto" aria-expanded={showPrefs} onClick={() => setShowPrefs((v) => !v)}>Preferences</button>
      </div>
      {showPrefs && <NotificationPreferences />}

      {burstIds && (
        <div className="nt-banner">
          Showing the notifications from one burst.
          <button type="button" className="nt-link" onClick={() => ctx.navigate('/notifications')}>Show all</button>
        </div>
      )}

      <div className="nt-toolbar" role="toolbar" aria-label="Notification filters and actions">
        <div className="nt-seg" role="radiogroup" aria-label="Read status">
          {['all', 'unread', 'read'].map((s) => (
            <button key={s} type="button" role="radio" aria-checked={status === s} className={status === s ? 'nt-seg-on' : ''} onClick={() => setStatus(s)}>
              {s === 'all' ? 'All' : s === 'unread' ? 'Unread' : 'Read'}
            </button>
          ))}
        </div>
        <label className="nt-select-wrap">
          <span className="nt-sr">Category</span>
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="nt-select">
            <option value="">All categories</option>
            {(ctx.state.categories || Object.keys(CATEGORY_LABELS)).map((c) => <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>)}
          </select>
        </label>
        <div className="nt-actions">
          <label className="nt-check">
            <input type="checkbox" checked={allSelected} onChange={() => setSelected(allSelected ? new Set() : new Set(items.map((i) => i.id)))} />
            <span>Select all</span>
          </label>
          <button type="button" className="nt-link" onClick={() => selectByRead(false)}>Select unread</button>
          <button type="button" className="nt-link" onClick={() => selectByRead(true)}>Select read</button>
          <button type="button" className="nt-btn" disabled={!selectedIds.length} onClick={() => doRead(selectedIds)}>Mark read</button>
          <button type="button" className="nt-btn nt-btn-danger" disabled={!selectedIds.length} onClick={() => setConfirm({ ids: selectedIds })}>Delete</button>
          <button type="button" className="nt-btn" disabled={!ctx.state.unread} onClick={() => doRead('all')}>Mark all as read</button>
          <button type="button" className="nt-btn nt-btn-danger" disabled={!items.length} onClick={() => setConfirm({ ids: 'all' })}>Delete all</button>
        </div>
      </div>

      {confirm && (
        <div className="nt-confirm" role="alertdialog" aria-label="Confirm delete">
          <span>{confirm.ids === 'all' ? 'Permanently delete all your notifications?' : `Permanently delete ${confirm.ids.length} notification(s)?`} This cannot be undone.</span>
          <button type="button" className="nt-btn nt-btn-danger" onClick={() => doDelete(confirm.ids)}>Delete</button>
          <button type="button" className="nt-btn" onClick={() => setConfirm(null)}>Cancel</button>
        </div>
      )}

      <ul className="nt-list">
        {items.map((it) => (
          <li key={it.id} className={`nt-item ${it.read ? '' : 'nt-unread'} ${it.severity === 'critical' ? 'nt-critical' : ''}`}>
            <input type="checkbox" className="nt-item-check" aria-label={`Select: ${it.title}`} checked={selected.has(it.id)} onChange={() => toggle(it.id)} />
            <button type="button" className="nt-item-main" title="Open this notification" onClick={() => ctx.openItem(it.id)}>
              <span className="nt-item-title">
                {!it.read && <span className="nt-dot" aria-label="Unread" />}
                {it.title}
              </span>
              <span className="nt-item-msg">{it.message}</span>
              <span className="nt-item-meta">
                {CATEGORY_LABELS[it.category] || it.category}
                {showFacility && it.facility_name ? ` · ${it.facility_name}` : ''}
                {' · '}
                {formatWhen(it.created_at)}
              </span>
            </button>
            <button type="button" className="nt-item-open" aria-label={`Open: ${it.title}`} onClick={() => ctx.openItem(it.id)}>
              Open
              <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path fill="currentColor" d="M9.3 6.3a1 1 0 0 1 1.4 0l5 5a1 1 0 0 1 0 1.4l-5 5a1 1 0 1 1-1.4-1.4L13.6 12 9.3 7.7a1 1 0 0 1 0-1.4Z" />
              </svg>
            </button>
          </li>
        ))}
      </ul>
      {!loading && !items.length && <p className="nt-empty">No notifications</p>}
      {loading && <p className="nt-empty">Loading…</p>}
      {cursor && !loading && <button type="button" className="nt-btn nt-load-more" onClick={() => load(false)}>Load more</button>}
    </div>
  );
}
